import { DateRangePicker } from "components/UI/DateRangePicker/DateRangePicker";
import { useTable } from "components/UI/Table/hooks/useTable";
import dayjs, { Dayjs } from "dayjs";
import { REPORT_DATE_FORMAT_WITH_TIME } from "localConstants";
import { useEffect, useMemo } from "react";
import { useRecoilState } from "recoil";
import { TableId } from "types/models";

import { timeFilterAtom, getPersistedTimeFilterValue, removePersistedTimeFilterValue } from "../../atoms/timeFilterAtom";
import { getDefaultCreatedAtFilter } from "../../utils/getDefaultCreatedAtFilter";

interface TimeFilterProps {
    tableId: TableId;
    dataIndex: string;
}

export const TimeFilter = ({ tableId, dataIndex }: TimeFilterProps) => {
    const tableUtils = useTable(tableId, dataIndex);
    const [timeFilter] = useRecoilState(timeFilterAtom);

    // "this month" fallback
    const defaultRange: [Dayjs, Dayjs] = [dayjs().subtract(30, "day").startOf("day"), dayjs().endOf("day")];

    // Helper to parse and validate date range string
    const parseDateRange = (value: string): [Dayjs, Dayjs] | null => {
        const parts = value.split(",");
        if (parts.length !== 2) return null;

        const start = dayjs(parts[0].trim(), REPORT_DATE_FORMAT_WITH_TIME);
        const end = dayjs(parts[1].trim(), REPORT_DATE_FORMAT_WITH_TIME);

        if (!start.isValid() || !end.isValid()) return null;
        return [start, end];
    };

    // Update the timeFilter's dataIndex when switching tables with different dataIndex
    useEffect(() => {
        if (timeFilter) {
            // Update the timeFilter to use the correct dataIndex for this table
            const updatedRule = {
                ...timeFilter,
                dataIndex: dataIndex,
                filterName: dataIndex,
            };
            tableUtils.saveTimeFilter(updatedRule);
        }
    }, [dataIndex]);

    // Initialize timeFilter from persisted value on mount
    useEffect(() => {
        const persistedValue = getPersistedTimeFilterValue();
        if (persistedValue && (!timeFilter || timeFilter.dataIndex !== dataIndex)) {
            const parsed = parseDateRange(persistedValue);
            if (!parsed) {
                console.warn("Invalid persisted time filter value, clearing");
                removePersistedTimeFilterValue();
                return;
            }
            const [startDate, endDate] = parsed;
            const newRule = getDefaultCreatedAtFilter([startDate, endDate], dataIndex);
            tableUtils.saveTimeFilter(newRule);
        }
    }, [dataIndex]);

    // derive what the picker shows
    const dateRange: [Dayjs, Dayjs] = useMemo(() => {
        // timeFilter state is the source of truth
        if (timeFilter?.value && typeof timeFilter.value === "string") {
            const parsed = parseDateRange(timeFilter.value);
            if (parsed) return parsed;
        }
        // Fallback to persisted value only for initial load (before timeFilter is set)
        const persistedValue = getPersistedTimeFilterValue();
        if (persistedValue) {
            const parsed = parseDateRange(persistedValue);
            if (parsed) return parsed;
        }
        return defaultRange;
    }, [timeFilter, dataIndex]);

    // only runs when the user *chooses* new dates
    const handleChange = (dates: [Dayjs | null, Dayjs | null]) => {
        if (!dates?.[0] || !dates[1]) return;

        const start = dates[0].startOf("day");
        const end = dates[1].endOf("day");
        const newRule = getDefaultCreatedAtFilter([start, end], dataIndex);

        // saveTimeFilter will update the state and persist to localStorage
        tableUtils.saveTimeFilter(newRule);
    };

    return (
        <div style={{ display: "flex", gap: 12 }}>
            <DateRangePicker value={dateRange} onChange={handleChange} allowClear={false} />
        </div>
    );
};
